import { Skeleton } from "antd"
import React, { type ReactNode, Suspense, lazy, useMemo } from "react"

import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import { DashboardHeader } from "./DashboardHeader.tsx"
import { type DashboardTableFilterKey } from "./DashboardHeaderSearch.tsx"
import { DashboardNotifications } from "./DashboardNotifications.tsx"
import { DashboardStats } from "./DashboardStats/DashboardStats.tsx"

const DashboardNewLeads = lazy(
  () => import("./DashboardNewLeads/DashboardNewLeads.tsx")
)

const DashboardRecentProjects = lazy(
  () => import("./DashboardRecentProjects/DashboardRecentProjects.tsx")
)

function useHomeSections() {
  const user = useAppSelector((state) => state.auth.userInfo)
  const permissions = useAppSelector((state) => state.auth?.permissions)

  const features = permissions?.codes || {}
  const moduleLeads = features?.leads?.leads
  const moduleProjects = features?.projects?.projects

  const tableKey = useMemo<DashboardTableFilterKey | null>(() => {
    if (moduleProjects?.project_view) return "projectFilters"
    if (moduleLeads?.lead_view) return "leadFilters"
    return null
  }, [moduleProjects, moduleLeads])

  const setting = tableKey
    ? {
        leadFilters: {
          create: moduleLeads?.lead_create || false,
          search: moduleLeads?.lead_view || false,
          update: moduleLeads?.lead_edit || false
        },
        projectFilters: {
          create: moduleProjects?.project_create || false,
          search: moduleProjects?.project_view || false,
          update: moduleProjects?.project_edit || false
        }
      }[tableKey]
    : undefined

  return { user, features, tableKey, setting }
}

export function HomeGreeting({
  viewControls,
  exportControl
}: {
  viewControls?: ReactNode
  exportControl?: ReactNode
}) {
  const { user, tableKey, setting } = useHomeSections()

  return (
    <DashboardHeader
      canCreate={setting?.create || false}
      canSearch={setting?.search || false}
      tableFilterKey={tableKey}
      user={user}
      viewControls={viewControls}
      exportControl={exportControl}
    />
  )
}

/**
 * The left column's tail: the shortcut row and whichever list the caller's
 * permissions resolve to. Split out of the old `HomeBelowSections` so the
 * two-column body can place it and Notifications in different columns.
 */
export function HomeShortcutsAndProjects() {
  const { features, setting, tableKey } = useHomeSections()

  const tables: Record<DashboardTableFilterKey, ReactNode> = {
    leadFilters: <DashboardNewLeads />,
    projectFilters: (
      <DashboardRecentProjects
        showProgress={!!features?.projects?.features?.project_show_progress}
        canUpdate={!!setting?.update}
      />
    )
  }

  return (
    <>
      <DashboardStats permissionFeatures={features} />
      {setting?.search && tableKey ? (
        <Suspense fallback={<Skeleton.Node className={"size-full"} />}>
          {tables?.[tableKey]}
        </Suspense>
      ) : null}
    </>
  )
}

/** The right column's tail, under Next Best Actions. */
export function HomeNotificationsPanel() {
  return <DashboardNotifications />
}
